import Phaser from 'phaser';
import { VIEW_HALF_RANGE_X, VIEW_HALF_RANGE_Y } from '@shared/constants';
import type { MapData, MapAreaData } from '@shared/types/map';
import { getAreasAtTile } from '@shared/utils/areaUtils';
import { bakeMapTexture } from './minimapBake';

/**
 * Minimapa in-game. Sigue al jugador (siempre centrado) con zoom FIJO (radio amplio), marca
 * NPCs neutrales interactivos (merchant/quest) y deja preparada — detrás de booleanos — la
 * lógica para features futuras (radar de hostiles, otros players, party, clan). Ciertas áreas
 * lo ocultan (queda negro).
 *
 * Skin (ver `docs/UI_SKINNING.md` y `docs/MINIMAP.md`): arte de `assets/ui/minimap/` con la
 * convención frame + posiciones en px NATIVOS × `FRAME_SCALE` (igual que `TargetPanel`):
 *   - `mp2_frame` (414×414): frame ornamentado; el mapa va ENCIMA, recortado a la ventana interna.
 *   - `mp2_map_overlay` (348×350): viñeta sobre el mapa.
 *   - `mp1_button` (+HOVER/PUSH) + `mp_icon_*`: 4 botones (brújula = world map; resto placeholder).
 */

/** Entidad mínima para marcar en el minimapa. */
export interface MinimapEntity {
  x: number;
  y: number;
  /** Color del marcador (override). Default azul. Usado por aliados de facción. */
  color?: number;
}

export interface MinimapNpc extends MinimapEntity {
  /** 0 = amigable/vendedor, 1 = hostil (mob). */
  attackable: number;
  /** 1 = interactivo (merchant / spell-vendor / quest). */
  interact: number;
}

export interface MinimapUpdateContext {
  /**
   * Posición del player en coordenadas de tile, **continua/sub-tile** (no redondeada), para
   * que el minimapa siga el movimiento interpolado del personaje de forma suave (sin saltos
   * de a un tile). Tile N centrado ⇒ valor N.
   */
  playerX: number;
  playerY: number;
  npcs: Iterable<MinimapNpc>;
  /** Otros jugadores visibles (sin el local). Sólo se marcan si `showOtherPlayers`. */
  otherPlayers: Iterable<MinimapEntity>;
  /** Áreas del mapa actual (para el blackout). */
  areas: MapAreaData[] | undefined;
}

export interface MinimapOptions {
  /** Handler del botón de brújula (abrir el world map). */
  onOpenWorldMap?: () => void;
}

// --- Texture keys (assets/ui/minimap) ---
const FRAME_KEY = 'mp2_frame';
const OVERLAY_KEY = 'mp2_map_overlay';
const BTN_KEY = 'mp1_button';
const BTN_HOVER_KEY = 'mp1_button-HOVER';
const BTN_PUSH_KEY = 'mp1_button-PUSH';

// --- Layout en px NATIVOS del frame (414×414) × FRAME_SCALE (tuneable contra el arte) ---
const FRAME_W = 414;
const FRAME_H = 414;
const FRAME_SCALE = 0.35; // ≈145px en pantalla
const MAP_X = 30; // ventana interna donde se dibuja el mapa
const MAP_Y = 28;
const MAP_W = 354;
const MAP_H = 356;
const BTN_NATIVE = 64; // tamaño display de la base del botón (un poco más chico que el arte)
const ICON_REL = 0.5; // ícono = 50% del botón
const SCREEN_MARGIN = 12;
// Capa UI (uiCamera, depth >= 800), pero DEBAJO de los paneles del juego
// (InventoryPanel/CharacterInfoPanel arrancan en ~850; el resto va más arriba).
// Así cualquier ventana que se abra tapa el minimapa, en vez de quedar siempre
// visible por encima. El minimapa usa DEPTH..DEPTH+5.1 internamente, así que el
// tope (≈825) queda por debajo de 850. Subir esto lo volvería a dejar arriba.
const DEPTH = 820;

// --- Zoom FIJO (px por tile en pantalla) ---
// No es variable: el minimapa siempre muestra el mismo radio amplio alrededor del player.
// A 1 px/tile, la ventana del mapa (~177px a FRAME_SCALE 0.5) abarca ~177 tiles de lado.
const FIXED_ZOOM = 1;

// --- Marcadores ---
const COLOR_NEUTRAL_NPC = 0xffd24a; // amarillo: NPC neutral interactivo
const COLOR_HOSTILE = 0xff3b3b; // rojo: hostiles (radar)
const COLOR_OTHER_PLAYER = 0x4ab0ff; // azul: otros players
const COLOR_PLAYER = 0xffffff; // blanco: jugador local
const COLOR_VISION = 0x9fd6ff; // celeste suave: recuadro del área de visión actual

interface MinimapButton {
  base: Phaser.GameObjects.Image;
  icon: Phaser.GameObjects.Image;
  nx: number; // centro nativo
  ny: number;
}

export class Minimap {
  // Toggles de features futuras (default off). Una habilidad/item/lógica social los activará.
  public hostileRadarEnabled = false;
  public hostileRadarRadiusTiles = 12;
  public showOtherPlayers = false;

  private readonly scene: Phaser.Scene;
  private readonly zoom = FIXED_ZOOM;
  private readonly onOpenWorldMap?: () => void;

  // Resolución de la textura horneada (px por tile) y su key (compartida con MapOverlay).
  private pxPerTile = 1;
  private terrainKey: string | null = null;

  // Esquina superior-izquierda del frame en screen-space (scrollFactor 0).
  private frameX = 0;
  private frameY = 0;

  // GameObjects.
  private frame: Phaser.GameObjects.Image;
  private mapImage: Phaser.GameObjects.Image | null = null;
  private markers: Phaser.GameObjects.Graphics;
  private overlay: Phaser.GameObjects.Image;
  private blackout: Phaser.GameObjects.Rectangle;
  private maskShape: Phaser.GameObjects.Graphics;
  private mask: Phaser.Display.Masks.GeometryMask;
  private readonly buttons: MinimapButton[] = [];

  // Drag: mover el minimapa por la pantalla arrastrándolo del frame.
  private dragging = false;
  private dragDX = 0;
  private dragDY = 0;
  private readonly onPointerMove: (pointer: Phaser.Input.Pointer) => void;
  private readonly onPointerUp: () => void;

  constructor(scene: Phaser.Scene, opts: MinimapOptions = {}) {
    this.scene = scene;
    this.onOpenWorldMap = opts.onOpenWorldMap;

    this.frameX = scene.scale.width - SCREEN_MARGIN - FRAME_W * FRAME_SCALE;
    this.frameY = SCREEN_MARGIN;

    // Frame: arranca el drag y absorbe clicks (no van al mundo).
    this.frame = scene.add
      .image(this.frameX, this.frameY, FRAME_KEY)
      .setOrigin(0, 0)
      .setScale(FRAME_SCALE)
      .setScrollFactor(0)
      .setDepth(DEPTH)
      .setInteractive({ useHandCursor: true });
    this.frame.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.dragging = true;
      this.dragDX = pointer.x - this.frameX;
      this.dragDY = pointer.y - this.frameY;
    });

    this.markers = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH + 2);

    this.overlay = scene.add
      .image(0, 0, OVERLAY_KEY)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTH + 3);

    this.blackout = scene.add
      .rectangle(0, 0, 10, 10, 0x000000, 1)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH + 4)
      .setVisible(false);

    // Máscara que recorta el terreno + marcadores a la ventana del mapa.
    this.maskShape = scene.make.graphics({}, false);
    this.mask = this.maskShape.createGeometryMask();
    this.markers.setMask(this.mask);

    // Botones (orden: brújula arriba-derecha; lupa/estrella/engranaje abajo al centro).
    // Centros nativos. Los 3 de abajo se separan (D=76 > BTN_NATIVE) para que no se encimen.
    this.createButton('mp_icon_map', 381, 36, () => this.onOpenWorldMap?.());
    this.createButton('mp_icon_search', 131, 382, () => {
      // TODO(minimap-buttons): cablear cuando definamos la función de la lupa.
    });
    this.createButton('mp_icon_star', 207, 382, () => {
      // TODO(minimap-buttons): cablear cuando definamos la función de la estrella.
    });
    this.createButton('mp_icon_settings', 283, 382, () => {
      // TODO(minimap-buttons): cablear cuando definamos la función del engranaje.
    });

    this.layout();

    // Drag: reposicionar el minimapa siguiendo el puntero (clamp a la pantalla).
    this.onPointerMove = (pointer): void => {
      if (!this.dragging) return;
      const maxX = this.scene.scale.width - FRAME_W * FRAME_SCALE;
      const maxY = this.scene.scale.height - FRAME_H * FRAME_SCALE;
      this.frameX = Phaser.Math.Clamp(pointer.x - this.dragDX, 0, maxX);
      this.frameY = Phaser.Math.Clamp(pointer.y - this.dragDY, 0, maxY);
      this.layout();
    };
    this.onPointerUp = (): void => {
      this.dragging = false;
    };
    scene.input.on('pointermove', this.onPointerMove);
    scene.input.on('pointerup', this.onPointerUp);
  }

  /**
   * Hornea (o reusa) la textura del mapa con **sprites reales** y crea la imagen del
   * minimapa. Misma textura cacheada que usa el world-map overlay. Llamar al cargar mapa.
   */
  setMap(map: MapData): void {
    const baked = bakeMapTexture(this.scene, map);
    this.terrainKey = baked.key;
    this.pxPerTile = baked.pxPerTile;

    if (this.mapImage) {
      this.mapImage.destroy();
      this.mapImage = null;
    }
    this.mapImage = this.scene.add
      .image(0, 0, this.terrainKey)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH + 1);
    this.mapImage.setMask(this.mask);

    // NOTA: NO aplicar postFX (p.ej. addBlur) sobre mapImage. Un FX por-objeto en
    // Phaser renderiza la textura a un framebuffer aparte que IGNORA el geometry
    // mask → el terreno se ve entero, fuera de la ventana del minimapa. El mask es
    // funcional (recorte), el blur era solo cosmético: se prioriza el recorte.
  }

  /** Refresca el minimapa: centra al player y redibuja marcadores. Llamar cada frame. */
  update(ctx: MinimapUpdateContext): void {
    // ptx/pty son continuos (sub-tile) → el seguimiento es suave, no salta de a un tile.
    const { playerX: ptx, playerY: pty } = ctx;
    const r = this.mapRect();
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;

    // Centrar el terreno en la posición exacta del jugador. La textura está horneada a
    // `pxPerTile` px/tile; escalando por `zoom/pxPerTile` quedan `zoom` px/tile en pantalla.
    if (this.mapImage) {
      this.mapImage.setScale(this.zoom / this.pxPerTile);
      this.mapImage.setPosition(cx - (ptx + 0.5) * this.zoom, cy - (pty + 0.5) * this.zoom);
    }

    // Blackout por área: usa el tile entero donde está el player (redondeo de la pos continua).
    const hidden = getAreasAtTile(ctx.areas, Math.round(ptx), Math.round(pty)).some(
      (a) => a.hideMinimap === true
    );
    this.blackout.setVisible(hidden);
    this.markers.clear();
    if (hidden) return;

    const halfW = r.w / 2;
    const halfH = r.h / 2;
    const toScreen = (tx: number, ty: number): { x: number; y: number } => ({
      x: cx + (tx - ptx) * this.zoom,
      y: cy + (ty - pty) * this.zoom,
    });
    const inViewport = (sx: number, sy: number): boolean =>
      Math.abs(sx - cx) <= halfW && Math.abs(sy - cy) <= halfH;

    // NPCs: neutral interactivo siempre; hostil sólo con radar activo y dentro del radio.
    for (const npc of ctx.npcs) {
      const neutralInteractive = npc.attackable === 0 && npc.interact === 1;
      const hostile = npc.attackable === 1;
      let color: number | null = null;
      if (neutralInteractive) {
        color = COLOR_NEUTRAL_NPC;
      } else if (hostile && this.hostileRadarEnabled) {
        const dist = Math.max(Math.abs(npc.x - ptx), Math.abs(npc.y - pty));
        if (dist <= this.hostileRadarRadiusTiles) color = COLOR_HOSTILE;
      }
      if (color === null) continue;
      const p = toScreen(npc.x, npc.y);
      if (!inViewport(p.x, p.y)) continue;
      this.markers.fillStyle(color, 1);
      // Indicador = 1 tile (this.zoom px), la unidad "1 pixel" del minimapa a esta escala.
      const z = this.zoom;
      this.markers.fillRect(p.x - z / 2, p.y - z / 2, z, z);
    }

    // Otros players: aliados de facción (visibles desde Teniente; ver
    // docs/FACTIONS.md) o futura lógica social. Cada entidad puede traer su color.
    if (this.showOtherPlayers) {
      for (const op of ctx.otherPlayers) {
        const p = toScreen(op.x, op.y);
        if (!inViewport(p.x, p.y)) continue;
        this.markers.fillStyle(op.color ?? COLOR_OTHER_PLAYER, 1);
        const z = this.zoom;
        this.markers.fillRect(p.x - z / 2, p.y - z / 2, z, z);
      }
    }

    // TODO(party): cuando exista el sistema de party, marcar a los miembros del party
    //   (probablemente con un color propio) iterando la lista de party en vez de / además
    //   de otros players. La estructura de marcado por entidad ya está lista acá.
    // TODO(clan): ídem para los miembros del clan cuando exista ese sistema.

    // Recuadro (muy fino) del área de visión actual: las tiles que se ven en pantalla.
    const visW = (2 * VIEW_HALF_RANGE_X + 1) * this.zoom;
    const visH = (2 * VIEW_HALF_RANGE_Y + 1) * this.zoom;
    this.markers.lineStyle(1, COLOR_VISION, 0.85);
    this.markers.strokeRect(cx - visW / 2, cy - visH / 2, visW, visH);

    // Jugador local: cuadrado de 1 tile (ancho y alto = zoom px), centrado. Sin heading.
    const tilePx = this.zoom;
    this.markers.fillStyle(COLOR_PLAYER, 1);
    this.markers.fillRect(cx - tilePx / 2, cy - tilePx / 2, tilePx, tilePx);
  }

  /** Reposiciona el minimapa (esquina del frame en screen-space). */
  setRect(x: number, y: number): void {
    this.frameX = x;
    this.frameY = y;
    this.layout();
  }

  /** Rect del frame en screen-space (para reparentar/encuadrar desde afuera). */
  getRect(): { x: number; y: number; w: number; h: number } {
    return { x: this.frameX, y: this.frameY, w: FRAME_W * FRAME_SCALE, h: FRAME_H * FRAME_SCALE };
  }

  getContainerDepth(): number {
    return DEPTH;
  }

  destroy(): void {
    this.scene.input.off('pointermove', this.onPointerMove);
    this.scene.input.off('pointerup', this.onPointerUp);
    this.frame.destroy();
    this.mapImage?.destroy();
    this.markers.destroy();
    this.overlay.destroy();
    this.blackout.destroy();
    this.maskShape.destroy();
    for (const b of this.buttons) {
      b.base.destroy();
      b.icon.destroy();
    }
    // La textura del terreno NO se remueve: está cacheada por id de mapa y la comparte
    // el world-map overlay (`MapOverlay`). Persiste para la sesión.
  }

  // =====================================================================

  /** Ventana del mapa (screen-space) a partir del frame + offsets nativos × escala. */
  private mapRect(): { x: number; y: number; w: number; h: number } {
    return {
      x: this.frameX + MAP_X * FRAME_SCALE,
      y: this.frameY + MAP_Y * FRAME_SCALE,
      w: MAP_W * FRAME_SCALE,
      h: MAP_H * FRAME_SCALE,
    };
  }

  /** Reposiciona frame, ventana del mapa (máscara), overlay, blackout y botones. */
  private layout(): void {
    this.frame.setPosition(this.frameX, this.frameY);

    const r = this.mapRect();
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;

    this.maskShape.clear();
    this.maskShape.fillStyle(0xffffff);
    this.maskShape.fillRect(r.x, r.y, r.w, r.h);

    this.overlay.setPosition(cx, cy).setDisplaySize(r.w, r.h);
    this.blackout.setPosition(r.x, r.y).setSize(r.w, r.h);

    for (const b of this.buttons) {
      const x = this.frameX + b.nx * FRAME_SCALE;
      const y = this.frameY + b.ny * FRAME_SCALE;
      b.base.setPosition(x, y);
      b.icon.setPosition(x, y);
    }
    // mapImage + markers se reubican en update() según el centro de la ventana.
  }

  private createButton(iconKey: string, nx: number, ny: number, action: () => void): void {
    const btnDisp = BTN_NATIVE * FRAME_SCALE;
    const setBase = (key: string): void => {
      base.setTexture(key);
      base.setDisplaySize(btnDisp, btnDisp); // mantener tamaño constante entre estados
    };

    const base = this.scene.add
      .image(0, 0, BTN_KEY)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTH + 5)
      .setInteractive({ useHandCursor: true });
    setBase(BTN_KEY);

    const icon = this.scene.add
      .image(0, 0, iconKey)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTH + 5.1)
      .setDisplaySize(btnDisp * ICON_REL, btnDisp * ICON_REL);

    base.on('pointerover', () => setBase(BTN_HOVER_KEY));
    base.on('pointerout', () => setBase(BTN_KEY));
    base.on('pointerdown', () => setBase(BTN_PUSH_KEY));
    base.on('pointerup', () => {
      setBase(BTN_HOVER_KEY);
      action();
    });

    this.buttons.push({ base, icon, nx, ny });
  }
}
